import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Package2, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useUpdateBundleSlugs, type LibraryRelease } from '@/hooks/useLibraryReleases';

interface EditBundleCompositionDialogProps {
  bundle: LibraryRelease;
  allReleases: LibraryRelease[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog per modificare includes_release_slugs di un bundle.
 *
 * Mostra checkbox raggruppate per content_type (Listino / Strat), filtrate per
 * stesso brand_slug del bundle. Al save sincronizza library_releases +
 * library_addons gemellato.
 */
export default function EditBundleCompositionDialog({
  bundle,
  allReleases,
  open,
  onOpenChange,
}: EditBundleCompositionDialogProps) {
  const candidates = allReleases.filter(
    (r) => r.brand_slug === bundle.brand_slug && (r.content_type === 'materials' || r.content_type === 'stratigraphies')
  );
  const [selected, setSelected] = useState<Set<string>>(new Set(bundle.includes_release_slugs ?? []));
  const update = useUpdateBundleSlugs();

  // Reset selected quando cambia il bundle (e.g. apertura su un bundle diverso)
  React.useEffect(() => {
    setSelected(new Set(bundle.includes_release_slugs ?? []));
  }, [bundle.id, bundle.includes_release_slugs]);

  const toggle = (slug: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleSave = async () => {
    await update.mutateAsync({ bundleId: bundle.id, slugs: Array.from(selected) });
    onOpenChange(false);
  };

  const listini = candidates.filter((r) => r.content_type === 'materials');
  const strat = candidates.filter((r) => r.content_type === 'stratigraphies');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifica composizione: {bundle.name}</DialogTitle>
          <DialogDescription>
            Seleziona quali release includere. Il cliente che paga il bundle riceverà entitlement per ciascuna release selezionata.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {listini.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-blue-700 font-semibold mb-2 flex items-center gap-1">
                <Package2 className="h-3.5 w-3.5" />
                Listini disponibili ({listini.length})
              </div>
              <div className="space-y-1.5">
                {listini.map((r) => (
                  <label key={r.slug} className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 px-2 py-1.5 rounded">
                    <Checkbox checked={selected.has(r.slug)} onCheckedChange={() => toggle(r.slug)} />
                    <span className="text-sm flex-1">{r.name}</span>
                    <Badge variant="outline" className="text-[10px]">v{r.version_year}{r.version_suffix ?? ''}</Badge>
                    {r.is_current_year && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">Corrente</Badge>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {strat.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-purple-700 font-semibold mb-2 flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" />
                Stratigrafie disponibili ({strat.length})
              </div>
              <div className="space-y-1.5">
                {strat.map((r) => (
                  <label key={r.slug} className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 px-2 py-1.5 rounded">
                    <Checkbox checked={selected.has(r.slug)} onCheckedChange={() => toggle(r.slug)} />
                    <span className="text-sm flex-1">{r.name}</span>
                    <Badge variant="outline" className="text-[10px]">v{r.version_year}{r.version_suffix ?? ''}</Badge>
                    {r.is_current_year && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">Corrente</Badge>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {candidates.length === 0 && (
            <div className="text-sm text-zinc-500 text-center py-4">
              Nessuna release Listino/Strat trovata per il brand di questo bundle ({bundle.brand_slug ?? '—'}). Crea prima le release.
            </div>
          )}

          <div className="text-xs text-zinc-500 bg-zinc-50 rounded p-2">
            <strong>{selected.size}</strong> release selezionate.
            Lo slug del bundle resta <code className="text-[10px]">{bundle.slug}</code>.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={update.isPending}>
            {update.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Salva composizione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
